import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDurationFormatter, useI18n } from "../../hooks/i18n-hooks";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useNavigation } from "@react-navigation/native";
import TimeSheetSpecificDatesComponent, {
    EnabledWorkingTimeSpecificType,
    WorkingTimeSpecificDate,
} from "../../components/working_time/TimeSheetSpecificDatesComponent";
import { differenceInDays, isWithinInterval } from "date-fns";
import { useMutation, useQuery } from "@apollo/client";
import {
    GET_ALL_TIMESHEETS,
    GET_DURATION_BREAKDOWN,
    GET_MISSION_VACATION_STATUS,
    GET_TIME_SHEET,
    SAVE_TIME_SHEET,
    SUBMIT_EXACT_TIME_SHEET,
} from "../../graphql/timesheet";
import {
    DateScheduleType,
    DayScheduleType,
    EarningType,
    ProfileType,
    Repetition,
    ScheduleTimeType,
    ScheduleType,
    TimeMode,
    TimeSheet,
    TimeSheetState,
} from "../../__generated__/graphql";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import VStack from "../../ui_atoms/VStack";
import { SwipeButton } from "../../components/SwipeButton";
import SimplePageContent from "../../components/SimplePageContent";
import TimeSheetReview from "../../components/TimeSheetReview";
import Callout from "../../ui_atoms/Callout";
import Loading from "../../components/Loading";
import { useAsyncError } from "../../hooks/application";
import ScreenHeader from "./ScreenHeader";
import { useApolloErrorSnackbar, useSnackbar } from "../../hooks/snackbar";
import { GET_ACTIVITY_TODO_FEED, GET_ALL_PROFILES_ACTIVITIES_INFO } from "../../graphql/activity";
import { GET_MISSION } from "../../graphql/core";
import { useTracking } from "../../hooks/tracking";
import { useTheme } from "../../hooks/use-theme";
import Gap from "../../ui_atoms/Gap";
import { DEFAULT_HOURS_PER_WEEK, getWeekNumber } from "../../utils";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import MissionInfoItem from "../../components/MissionInfoItem";
import { useTimesheetsRange } from "../../hooks/date";
import ListItem from "../../ui_atoms/ListItem";
import { checkScheduleType } from "../../components/working_time/helpers";
import { diff } from "deep-diff";

type TimesheetValidationDetailProps = StackScreenProps<RootStackParamList, "TimesheetWorkerInput">;

type ScreenParams = StackScreenProps<RootStackParamList>;

const sanitizeScheduleType = (st: ScheduleType): ScheduleType => {
    return {
        startTime: st.startTime,
        endTime: st.endTime,
        type: st.type,
    };
};

const sanitizeDayScheduleType = (dst?: DayScheduleType | null): DayScheduleType | undefined => {
    if (!dst) {
        return undefined;
    }
    return {
        freeDurations: dst.freeDurations,
        timeMode: dst.timeMode,
        fixedTimes: dst.fixedTimes?.map((x) => sanitizeScheduleType(x)),
    };
};

const buildTrackingDates = (dates: EnabledWorkingTimeSpecificType[]): Array<DateScheduleType> => {
    return dates.reduce((acc, { enabled, disabled: _disabled, ...w }) => {
        if (enabled) {
            acc.push({ date: w.date, daySchedule: sanitizeDayScheduleType(w.daySchedule) });
        }
        return acc;
    }, [] as Array<DateScheduleType>);
};

const TimesheetWorkerInput = ({ route }: TimesheetValidationDetailProps) => {
    const i18n = useI18n();
    const throwError = useAsyncError();
    const { gridSize } = useTheme();
    const { profileType, refreshUnreadProfilesActivities } = useContext(
        ProfileSwitcherManagerContext,
    );
    const navigation = useNavigation<ScreenParams["navigation"]>();
    const [weekAvailableWorkingTime, setWeekAvailableWorkingTime] = useState(0);
    const [autoSave, setAutoSave] = useState(false);

    const { data, error, loading } = useQuery(GET_TIME_SHEET, {
        variables: { timeSheetId: route.params.id },
        fetchPolicy: "network-only",
    });
    const timesheet: TimeSheet | undefined = data?.getTimeSheet;
    const getTimesheetsRange = useTimesheetsRange();

    const {
        data: vacationStatusData,
        error: vacationStatusError,
        loading: vacationStatusLoading,
    } = useQuery(GET_MISSION_VACATION_STATUS, {
        variables: { missionId: timesheet?.missionInfo.id || "" },
        skip: !timesheet,
        fetchPolicy: "cache-and-network",
    });
    const vacationStatus = vacationStatusData?.getMissionVacationStatus;

    useEffect(() => {
        if (!loading && error) {
            navigation &&
                navigation.replace?.("NotFoundScreen", {
                    error: error,
                    goBack: true,
                    isNotFound: true,
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, loading, error]);

    const isLocked = useMemo(
        () =>
            timesheet?.status === TimeSheetState.Accepted ||
            timesheet?.status === TimeSheetState.Submitted ||
            profileType === ProfileType.Client,
        [profileType, timesheet?.status],
    );

    const { loading: getMissionLoading, data: getMissionData } = useQuery(GET_MISSION, {
        variables: {
            id: timesheet?.missionInfo.id || "",
        },
        skip: !timesheet?.missionInfo.id,
    });

    const { missionStartDate, missionEffectiveEndDate, missionMinutesPerWeekFullTime } =
        useMemo(() => {
            return {
                missionStartDate: getMissionData?.getMission?.currentContract?.startDate
                    ? new Date(getMissionData.getMission.currentContract.startDate)
                    : undefined,
                missionEffectiveEndDate: getMissionData?.getMission?.currentContract
                    ?.effectiveEndDate
                    ? new Date(getMissionData.getMission.currentContract.effectiveEndDate)
                    : undefined,
                missionMinutesPerWeekFullTime: getMissionData?.getMission?.currentContract
                    ? (getMissionData?.getMission.currentContract.hoursPerWeekFullTime ||
                          DEFAULT_HOURS_PER_WEEK) * 60
                    : DEFAULT_HOURS_PER_WEEK * 60,
            };
        }, [getMissionData]);

    useEffect(
        () => {
            if (timesheet?.fromDate && timesheet?.toDate && missionStartDate) {
                const initialWorkingTime: EnabledWorkingTimeSpecificType[] = [];

                let daysDifference = differenceInDays(
                    new Date(timesheet.toDate),
                    new Date(timesheet.fromDate),
                );
                const currentDay = new Date(timesheet.fromDate);
                let nbAvailableWorkingDays = 0;
                for (; daysDifference >= 0; daysDifference--) {
                    const dayAsString = currentDay.toISOString().split("T")[0];
                    const isAvailableDay = isWithinInterval(currentDay, {
                        start: missionStartDate,
                        end: missionEffectiveEndDate || new Date(5000, 0, 1),
                    });
                    if (isAvailableDay && currentDay.getDay() !== 0 && currentDay.getDay() !== 6) {
                        nbAvailableWorkingDays++;
                    }
                    const prefilledDate = timesheet.trackingDates.find(
                        (w) => w.date === dayAsString,
                    );
                    // This code is ridiculous, we should use template or copy from previous days
                    const morningStartMinutes = 8 * 60;
                    const afternoonStartMinutes = 13 * 60;
                    let morningMinutes = 4 * 60;
                    let afternoonMinutes = Math.round(
                        missionMinutesPerWeekFullTime / 5 - morningMinutes,
                    );
                    if (afternoonMinutes < 0) {
                        morningMinutes += afternoonMinutes;
                        afternoonMinutes = 0;
                    }
                    initialWorkingTime.push({
                        enabled: !!prefilledDate,
                        disabled: !isAvailableDay,
                        date: dayAsString,
                        daySchedule: {
                            timeMode: TimeMode.Fixed,
                            fixedTimes: [
                                {
                                    startTime: morningStartMinutes,
                                    endTime: morningStartMinutes + morningMinutes,
                                    type: ScheduleTimeType.Work,
                                },
                                ...(afternoonMinutes > 0
                                    ? [
                                          {
                                              startTime: afternoonStartMinutes,
                                              endTime: afternoonStartMinutes + afternoonMinutes,
                                              type: ScheduleTimeType.Work,
                                          },
                                      ]
                                    : []),
                            ],
                        },
                        ...(prefilledDate || {}),
                    });
                    currentDay.setDate(currentDay.getDate() + 1);
                }
                setWeekAvailableWorkingTime(
                    (timesheet.contractWeeklyDurationMinutes * nbAvailableWorkingDays) / 5,
                );
                setWorkingTime({ dates: initialWorkingTime });
            }
        },
        // we don't want to recompute default workingtime when workingTime changes
        //eslint-disable-next-line react-hooks/exhaustive-deps
        [
            missionEffectiveEndDate,
            missionStartDate,
            timesheet?.fromDate,
            timesheet?.toDate,
            timesheet?.contractWeeklyDurationMinutes,
            setWeekAvailableWorkingTime,
        ],
    );

    const [workingTime, setWorkingTime] = useState<WorkingTimeSpecificDate>({ dates: [] });

    const isTooEarly = useMemo(
        () =>
            workingTime.dates
                .filter((date) => date.enabled)
                .some((date) => new Date(date.date) > new Date()),
        [workingTime],
    );

    const availableVacationMinutes = useMemo(() => {
        if (!vacationStatus) {
            return 0;
        }
        return (
            vacationStatus.availableVacationMinutes + vacationStatus.availablePendingVacationMinutes
        );
    }, [vacationStatus]);

    const getVacationOverflow = useCallback(
        (wtsd: WorkingTimeSpecificDate): number => {
            if (!vacationStatus) {
                return 0;
            }
            if (!timesheet?.missionInfo.earningType) {
                return 0;
            }
            if (
                ![EarningType.Hourly, EarningType.Daily].includes(
                    timesheet?.missionInfo.earningType,
                )
            ) {
                // report only for hourly and daily
                return 0;
            }
            const vacationDays = wtsd.dates.filter((date) => date.enabled);
            const vacationMinutes = vacationDays.reduce((acc, date) => {
                return (
                    acc +
                    (date.daySchedule?.fixedTimes?.reduce((acc, time) => {
                        if (time.type === ScheduleTimeType.Vacation) {
                            return acc + (time.endTime - time.startTime);
                        }
                        return acc;
                    }, 0) || 0)
                );
            }, 0);
            const left =
                vacationStatus.availableVacationMinutes +
                vacationStatus.availablePendingVacationMinutes -
                vacationMinutes;
            if (left < 0) {
                return Math.abs(left);
            }
            return 0;
        },
        [timesheet?.missionInfo.earningType, vacationStatus],
    );

    const vacationOverflow = useMemo(() => {
        return getVacationOverflow(workingTime);
    }, [getVacationOverflow, workingTime]);

    const [submitTimesheet, { loading: submitTimeSheetLoading }] = useMutation(
        SUBMIT_EXACT_TIME_SHEET,
        {
            refetchQueries: [
                GET_TIME_SHEET,
                GET_ALL_TIMESHEETS,
                GET_ACTIVITY_TODO_FEED,
                GET_ALL_PROFILES_ACTIVITIES_INFO,
            ],
        },
    );

    const [saveTimesheet, { loading: saveTimeSheetLoading, error: saveTimeSheetError }] =
        useMutation(SAVE_TIME_SHEET, {
            refetchQueries: [],
        });
    useApolloErrorSnackbar(saveTimeSheetError);

    const _trackingDates = useMemo(() => {
        return buildTrackingDates(workingTime.dates);
    }, [workingTime.dates]);

    const _saveData = useMemo(
        () => (data: WorkingTimeSpecificDate) => {
            const _func = async () => {
                try {
                    const variables = {
                        timeSheetId: route.params.id,
                        trackingDates: buildTrackingDates(data.dates),
                    };
                    await saveTimesheet({
                        variables,
                    });
                } finally {
                    setAutoSave(false);
                }
            };
            void _func();
        },
        [route.params.id, saveTimesheet],
    );
    const { showSnackbar } = useSnackbar();
    const { trackEvent } = useTracking();
    const onSubmitTimesheet = useCallback(async () => {
        const variables = {
            timeSheetId: route.params.id,
            trackingDates: _trackingDates,
        };
        try {
            const result = await submitTimesheet({
                variables,
            });
            void trackEvent("click_submit_timesheet");
            if (!result.errors) {
                showSnackbar({
                    text: i18n.t(`screens.user.timesheet_review.submitted_info`),
                    mode: "success",
                });
                void refreshUnreadProfilesActivities();
                navigation.goBack();
            }
        } catch (e) {
            throwError(e as Error);
        }
    }, [
        _trackingDates,
        navigation,
        i18n,
        route.params.id,
        showSnackbar,
        submitTimesheet,
        refreshUnreadProfilesActivities,
        throwError,
        trackEvent,
    ]);

    const onClose = useCallback(() => {
        navigation.goBack();
    }, [navigation]);

    const [hasError, setHasError] = useState(false);
    const skipBreakdown = !timesheet?.missionInfo.id || hasError;

    const {
        loading: getDurationBreakdownLoading,
        data: getDurationBreakdownData,
        error: getDurationBreakdownError,
    } = useQuery(GET_DURATION_BREAKDOWN, {
        variables: {
            trackingDates: _trackingDates,
            missionId: timesheet?.missionInfo.id || "",
        },
        skip: skipBreakdown,
    });

    const submitting = submitTimeSheetLoading || saveTimeSheetLoading;
    const containsError = useCallback(
        (wtsd: WorkingTimeSpecificDate): boolean => {
            const overflow = getVacationOverflow(wtsd);
            const errorInfo = checkScheduleType(wtsd.dates, overflow);
            return errorInfo.hasError;
        },
        [getVacationOverflow],
    );

    const updateWorkingTimeSpecificDate = useCallback(
        (data: WorkingTimeSpecificDate) => {
            setWorkingTime((old) => {
                if (!diff(old, data)) {
                    return old;
                }
                const dataContainsErrors = containsError(data);
                setHasError(dataContainsErrors);
                if (!dataContainsErrors) {
                    // do not save on error!!
                    setAutoSave(true);
                    _saveData(data);
                }
                return data;
            });
        },
        [_saveData, containsError],
    );

    const isWorkerInactive = useMemo(
        () =>
            workingTime.dates.every((w) => {
                return w.daySchedule?.fixedTimes?.length === 0 || !w.enabled || w.disabled;
            }),
        [workingTime],
    );
    const [_, week] = timesheet ? getWeekNumber(new Date(timesheet.fromDate)) : [null, null];

    const subLabel = useMemo(() => {
        const missionInfo = timesheet?.missionInfo;
        if (!missionInfo) {
            return i18n.t("loading");
        }
        return i18n.t("time_sheet.nav_sub_title", {
            week,
            range: timesheet ? getTimesheetsRange([timesheet], "long")?.formattedString : "",
        });
    }, [timesheet, week, getTimesheetsRange, i18n]);

    const disabledScheduleTimeTypes = useMemo(() => {
        if (timesheet?.missionInfo?.workingTimeClassicSpec?.repetition === Repetition.None) {
            return [ScheduleTimeType.Vacation];
        }
        return [];
    }, [timesheet?.missionInfo?.workingTimeClassicSpec?.repetition]);
    const formatDuration = useDurationFormatter();

    const showVacationInfo = useMemo(() => {
        if (!timesheet?.missionInfo?.workingTimeClassicSpec) {
            return false;
        }
        if (timesheet?.missionInfo.earningType === EarningType.Monthly) {
            return false;
        }
        if (timesheet?.missionInfo.earningType === EarningType.Package) {
            return false;
        }
        if (timesheet?.missionInfo?.workingTimeClassicSpec?.repetition == Repetition.None) {
            return false;
        }
        return true;
    }, [timesheet?.missionInfo.earningType, timesheet?.missionInfo?.workingTimeClassicSpec]);
    return (
        <>
            <ScreenHeader
                label={i18n.t("time_sheet.nav_title")}
                subLabel={subLabel}
                onLeftAction={onClose}
                leftIcon="close"
                leftActionDisabled={submitting || autoSave}
                hideProfile
                useSmallLabel
            />
            <LoadingBoundary
                loading={loading || getMissionLoading || vacationStatusLoading}
                error={error || vacationStatusError ? i18n.t("errors.unknown_error") : undefined}
            >
                {timesheet && (
                    <SimplePageContent>
                        <Gap gap={0.5} />
                        <MissionInfoItem missionInfo={timesheet?.missionInfo} />
                        {showVacationInfo && (
                            <ListItem
                                icon="vacation"
                                description={
                                    availableVacationMinutes > 0
                                        ? i18n.t("time_sheet.vacation_available", {
                                              value: formatDuration(availableVacationMinutes),
                                          })
                                        : i18n.t("time_sheet.no_vacation_available")
                                }
                            />
                        )}
                        <Gap gap={1} />
                        <TimeSheetSpecificDatesComponent
                            disabled={isLocked}
                            workingTime={workingTime}
                            onChange={updateWorkingTimeSpecificDate}
                            disabledScheduleTimeTypes={disabledScheduleTimeTypes}
                            vacationOverflow={vacationOverflow}
                        />
                        {!skipBreakdown &&
                            (getDurationBreakdownLoading ? (
                                <Loading />
                            ) : (
                                <VStack h={0.5} style={{ opacity: hasError ? 0.3 : 1.0 }}>
                                    <TimeSheetReview
                                        hoursBreakdown={
                                            getDurationBreakdownData?.getDurationBreakdown?.items
                                        }
                                        missionMinutesPerWeek={weekAvailableWorkingTime}
                                    />
                                </VStack>
                            ))}
                        {!isLocked && !isTooEarly && (
                            <VStack h="1/2" v="1" gap="1">
                                <SwipeButton
                                    onSwipeSuccess={onSubmitTimesheet}
                                    thumbIconName="swipe-right"
                                    title={i18n.t(
                                        !isWorkerInactive
                                            ? "screens.user.timesheet_review.send_for_review"
                                            : "screens.user.timesheet_review.declare_inactive",
                                    )}
                                    mode={!isWorkerInactive ? "success" : "warning"}
                                    testID="button-submit-timesheet"
                                    processing={submitting}
                                    disabled={hasError}
                                />
                            </VStack>
                        )}
                        {isTooEarly && (
                            <Callout
                                style={{ marginHorizontal: gridSize / 2, marginVertical: gridSize }}
                                mode="info"
                                markdown={i18n.t("screens.user.timesheet_review.too_early")}
                            />
                        )}
                        {getDurationBreakdownError && (
                            <Callout mode="error" markdown={getDurationBreakdownError.message} />
                        )}
                    </SimplePageContent>
                )}
            </LoadingBoundary>
        </>
    );
};

export default TimesheetWorkerInput;
